import { Link, createFileRoute } from "@tanstack/react-router";

import { FilterChips } from "~/components/filter-chips";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { StatusBadge } from "~/components/status-badge";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { EM_DASH, formatDateTime, formatRelative } from "~/lib/format";
import { usePaymentConflicts, type PaymentConflictFilters } from "~/lib/queries";
import { formatMinorIDR } from "./-format";
import { ResolveConflictActions } from "./-resolve-action";

const STATES = ["open", "resolved"] as const;

interface PaymentConflictSearch {
  state?: "open" | "resolved" | "all";
  order_id?: string;
  limit?: number;
  offset?: number;
}

function validateSearch(raw: Record<string, unknown>): PaymentConflictSearch {
  const state = raw.state === "resolved" || raw.state === "all" ? raw.state : "open";
  const num = (v: unknown) => {
    const n = typeof v === "number" ? v : typeof v === "string" ? Number.parseInt(v, 10) : NaN;
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  };
  return {
    state,
    order_id: typeof raw.order_id === "string" && raw.order_id !== "" ? raw.order_id : undefined,
    limit: num(raw.limit),
    offset: num(raw.offset),
  };
}

export const Route = createFileRoute("/_authed/payment-conflicts/")({
  validateSearch,
  component: PaymentConflictsPage,
});

const COLUMNS = 7;

/**
 * Late payments Altura refused to apply (PRO-1345).
 *
 * A row here is a buyer's money that arrived after the merchant was told the
 * order expired. Each one is work: keep it (Accept) or give it back (Mark
 * refunded). Until this screen existed the only fix was SQL. Opens on the open
 * ones, because those are the only reason to come here.
 */
function PaymentConflictsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters: PaymentConflictFilters = {
    state: search.state ?? "open",
    order_id: search.order_id,
    limit: search.limit,
    offset: search.offset,
  };
  const { data, error, isPending, isFetching, refetch } = usePaymentConflicts(filters);

  function patch(next: Partial<PaymentConflictSearch>) {
    void navigate({
      search: (previous) => ({ ...previous, ...next, offset: "offset" in next ? next.offset : 0 }),
      replace: true,
    });
  }

  const rows = data?.items ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Payment Conflicts"
        description="Payments DurianPay completed after Altura had already told the merchant the order expired. Accept one to keep it, or mark it refunded once the buyer has their money back."
      />

      <FilterChips
        value={search.state === "all" ? undefined : search.state}
        options={STATES}
        onChange={(next) => patch({ state: (next as PaymentConflictSearch["state"]) ?? "all" })}
      />

      {search.order_id ? (
        <p className="text-sm text-muted-foreground">
          Showing order <span className="font-mono text-foreground">{search.order_id}</span> only.{" "}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => patch({ order_id: undefined })}
          >
            Show every order
          </button>
        </p>
      ) : null}

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Recorded</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>DurianPay payment</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead>Transaction now</TableHead>
              <TableHead>Resolution</TableHead>
              <TableHead className="w-0">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError columns={COLUMNS} message={errorMessage(error)} onRetry={() => void refetch()} />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={
                  (search.state ?? "open") === "open"
                    ? "No open payment conflicts. Nothing is waiting on a decision."
                    : "No payment conflicts match."
                }
              />
            ) : (
              rows.map((row) => {
                const mismatch =
                  row.amount_minor != null &&
                  row.expected_minor != null &&
                  row.amount_minor !== row.expected_minor;
                return (
                  <TableRow key={row.id}>
                    <TableCell title={formatDateTime(row.created_at)}>
                      {formatRelative(row.created_at)}
                    </TableCell>

                    <TableCell>
                      <Link
                        to="/transactions/$orderId"
                        params={{ orderId: row.order_id }}
                        className="font-mono text-[0.8125rem] text-primary hover:underline"
                      >
                        {row.order_id}
                      </Link>
                    </TableCell>

                    <TableCell className="font-mono text-[0.8125rem] text-muted-foreground">
                      {row.psp_payment_id || EM_DASH}
                    </TableCell>

                    <TableCell
                      className={mismatch ? "text-right tabular-nums text-destructive" : "text-right tabular-nums"}
                      title={mismatch ? `The order is ${formatMinorIDR(row.expected_minor)}` : undefined}
                    >
                      {formatMinorIDR(row.amount_minor)}
                    </TableCell>

                    <TableCell>
                      {row.current_status ? <StatusBadge status={row.current_status} /> : EM_DASH}
                    </TableCell>

                    <TableCell className="text-muted-foreground">
                      {row.resolution ? (
                        <span title={typeof row.detail.resolution_note === "string" ? row.detail.resolution_note : undefined}>
                          {row.resolution} by {row.resolved_by ?? "someone"}{" "}
                          {formatRelative(row.resolved_at)}
                        </span>
                      ) : (
                        "open"
                      )}
                    </TableCell>

                    <TableCell className="text-right">
                      {row.resolved_at ? null : <ResolveConflictActions conflict={row} />}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar pagination={data.pagination} onChange={(next) => patch(next)} disabled={isFetching} />
        ) : null}
      </div>
    </div>
  );
}
